import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemIconSlot,
  DropdownMenuItemText,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tag,
  Typography,
  useLocaleFormat,
} from '@eye-able/ui';
import {
  Archive,
  ArchiveRestore,
  Copy,
  EllipsisVertical,
  Pencil,
  Trash2,
} from 'lucide-react';
import { IconZoomExclamation } from '@tabler/icons-react';
import { RouterLink } from '@/components/RouterLink';
import { useMemo, useState } from 'react';
import type { PolicyResponse } from '@http/queries/useFetchPolicies';
import { PolicyRuleDialog } from './PolicyRuleDialog';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { getPriorityTranslationKey } from '../priority';
import { PolicyRescanControl } from './PolicyRescanControl';
import { getUnlicensedPolicyDomainIds } from './policyLicenseCoverage';
import { useLicensedGovernanceDomainIds } from '@/http/queries/useFetchPolicyDomainAccess';
import type { PolicyDomainOption } from './usePolicyDomainOptions';
import { getNextAuditSchedule } from '@/helpers/auditSchedule';
import { AuditDate } from '@/components/AuditDate';
import { T } from '@/i18n/T';

function getTagVariant(value: string) {
  if (value === 'required' || value === 'active') return 'success';
  if (value === 'unwanted' || value === 'high') return 'warning';
  if (value === 'matches' || value === 'medium') return 'hint';
  if (value === 'archived') return 'error';
  return 'default';
}

function PolicyRowActions({
  policy,
  onDelete,
  onArchive,
  onUnarchive,
}: {
  policy: PolicyResponse;
  onDelete: (policy: PolicyResponse) => void;
  onArchive?: (policy: PolicyResponse) => void;
  onUnarchive?: (policy: PolicyResponse) => void;
}) {
  const { t } = useInlineTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='icon-xs'
          aria-label={t('actionsForPolicy', { policyName: policy.name })}
        >
          <EllipsisVertical aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='eye:min-w-[180px]'>
        <DropdownMenuItem
          render={<RouterLink to={`/policies/${policy.id}/edit`} />}
        >
          <DropdownMenuItemIconSlot>
            <Pencil aria-hidden className='eye:size-16' />
          </DropdownMenuItemIconSlot>
          <DropdownMenuItemText>{t('editPolicy')}</DropdownMenuItemText>
        </DropdownMenuItem>
        <DropdownMenuItem
          render={<RouterLink to={`/policies/new?duplicateId=${policy.id}`} />}
        >
          <DropdownMenuItemIconSlot>
            <Copy aria-hidden className='eye:size-16' />
          </DropdownMenuItemIconSlot>
          <DropdownMenuItemText>{t('duplicate')}</DropdownMenuItemText>
        </DropdownMenuItem>
        {onArchive ? (
          <DropdownMenuItem onClick={() => onArchive(policy)}>
            <DropdownMenuItemIconSlot>
              <Archive aria-hidden className='eye:size-16' />
            </DropdownMenuItemIconSlot>
            <DropdownMenuItemText>{t('archive')}</DropdownMenuItemText>
          </DropdownMenuItem>
        ) : null}
        {onUnarchive ? (
          <DropdownMenuItem onClick={() => onUnarchive(policy)}>
            <DropdownMenuItemIconSlot>
              <ArchiveRestore aria-hidden className='eye:size-16' />
            </DropdownMenuItemIconSlot>
            <DropdownMenuItemText>{t('unarchive')}</DropdownMenuItemText>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onDelete(policy)}
          className='eye:text-red-100 eye:data-[highlighted]:bg-red-25 eye:data-[highlighted]:text-red-100'
        >
          <DropdownMenuItemIconSlot>
            <Trash2 aria-hidden className='eye:size-16' />
          </DropdownMenuItemIconSlot>
          <DropdownMenuItemText>{t('delete')}</DropdownMenuItemText>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PolicyInventoryCard({
  policy,
  onDelete,
  onArchive,
  onUnarchive,
  canEdit,
  domainOptions,
}: {
  policy: PolicyResponse;
  onDelete: (policy: PolicyResponse) => void;
  onArchive?: (policy: PolicyResponse) => void;
  onUnarchive?: (policy: PolicyResponse) => void;
  canEdit: boolean;
  domainOptions: PolicyDomainOption[];
}) {
  const { t } = useInlineTranslation();
  const { formatNumber } = useLocaleFormat();
  const {
    licensedDomainIds,
    organisationDomainIds,
    expiredDomainIds,
    isLicenseUnavailable,
  } = useLicensedGovernanceDomainIds();
  const uncoveredDomainIds =
    policy.status === 'active' && !isLicenseUnavailable
      ? getUnlicensedPolicyDomainIds(
          policy,
          organisationDomainIds,
          licensedDomainIds,
        )
      : [];
  const isActiveWithoutLicense = uncoveredDomainIds.length > 0;
  const hasExpiredLicense = uncoveredDomainIds.some((id) =>
    expiredDomainIds.includes(id),
  );
  const statusLabel = isActiveWithoutLicense ? (
    <T
      k={
        hasExpiredLicense
          ? 'contentPolicyLicenceExpiredTitle'
          : 'contentPolicyLicenceMissingTitle'
      }
    />
  ) : (
    <T k={`policyStatus${policy.status}`} />
  );
  const priorityLabel = t(getPriorityTranslationKey(policy.priority));
  const selectedDomain = domainOptions.find(
    (domain) => domain.id === policy.auditEntryIds[0],
  );
  const scopeLabel =
    policy.domainScope === 'all-domains'
      ? t('allDomains')
      : policy.auditEntryIds.length === 1 && selectedDomain
        ? selectedDomain.label
        : t('selectedDomains', {
            count: policy.auditEntryIds.length,
          });
  const singleSelectedDomain =
    policy.domainScope === 'selected-domains' &&
    policy.auditEntryIds.length === 1
      ? selectedDomain?.auditEntry
      : undefined;
  const hasMultipleSelectedDomains =
    policy.domainScope === 'selected-domains' &&
    policy.auditEntryIds.length > 1;
  const lastCompletedAudit = singleSelectedDomain
    ? selectedDomain?.lastCompletedAudit
    : undefined;
  const nextAudit = useMemo(
    () => getNextAuditSchedule(singleSelectedDomain).nextAudit,
    [singleSelectedDomain],
  );
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);

  return (
    <Card className='eye:h-full'>
      <CardContent className='eye:flex eye:h-full eye:flex-col eye:gap-s eye:p-s'>
        <div className='eye:flex eye:items-start eye:justify-between eye:gap-s'>
          <div className='eye:min-w-0 eye:flex-1'>
            <Typography tag='h2' variant='headline-s'>
              {policy.name}
            </Typography>
            {policy.description ? (
              <Typography className='eye:mt-4xs eye:text-grey-120'>
                {policy.description}
              </Typography>
            ) : null}
          </div>
          {canEdit ? (
            <PolicyRowActions
              policy={policy}
              onDelete={onDelete}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
            />
          ) : null}
        </div>

        <div className='eye:grid eye:grid-cols-1 eye:gap-xs eye:text-14 eye:text-grey-120 eye:md:grid-cols-3'>
          <div className='eye:flex eye:flex-col eye:gap-5xs'>
            <Typography variant='body-s' className='eye:text-grey-120'>
              <T k='priority' />
            </Typography>
            <Typography>{priorityLabel}</Typography>
          </div>
          <div className='eye:flex eye:flex-col eye:gap-5xs'>
            <Typography variant='body-s' className='eye:text-grey-120'>
              <T k='status' />
            </Typography>
            <div>
              <Tag
                variant={
                  isActiveWithoutLicense
                    ? hasExpiredLicense
                      ? 'error'
                      : 'warning'
                    : getTagVariant(policy.status)
                }
                className='eye:min-w-0 eye:text-14'
              >
                {statusLabel}
              </Tag>
            </div>
          </div>
          <div className='eye:flex eye:flex-col eye:gap-5xs'>
            <Typography variant='body-s' className='eye:text-grey-120'>
              <T k='scope' />
            </Typography>
            <Typography>{scopeLabel}</Typography>
          </div>
          {lastCompletedAudit ? (
            <div className='eye:flex eye:flex-col eye:gap-5xs'>
              <Typography variant='body-s' className='eye:text-grey-120'>
                <T k='lastAudit' />
              </Typography>
              <AuditDate date={lastCompletedAudit} />
            </div>
          ) : null}
          {nextAudit ? (
            <div className='eye:flex eye:flex-col eye:gap-5xs'>
              <Typography variant='body-s' className='eye:text-grey-120'>
                <T k='nextAudit' />
              </Typography>
              <AuditDate
                date={nextAudit}
                allowedTime={singleSelectedDomain?.allowedTime}
              />
            </div>
          ) : null}
          {hasMultipleSelectedDomains ? (
            <Typography className='eye:md:col-span-3 eye:text-grey-120'>
              <T k='policyDomainStatusDetails' />
            </Typography>
          ) : null}
        </div>

        <div className='eye:mt-auto'>
          <div className='eye:flex eye:flex-wrap eye:justify-end eye:gap-xs'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setRuleDialogOpen(true)}
            >
              {t('showDetails')}
            </Button>
            {canEdit && policy.status === 'active' ? (
              <PolicyRescanControl policy={policy} />
            ) : null}
            {policy.openIssueCount ? (
              <Button asChild variant='black' size='sm'>
                <RouterLink
                  to={`/governance/custom-policies/issues?policyId=${policy.id}`}
                >
                  <IconZoomExclamation aria-hidden />
                  {t('viewOpenIssues', {
                    count: policy.openIssueCount,
                    formattedCount: formatNumber(policy.openIssueCount),
                  })}
                </RouterLink>
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>

      <PolicyRuleDialog
        policy={ruleDialogOpen ? policy : null}
        domainOptions={domainOptions}
        onOpenChange={(open) => {
          if (!open) setRuleDialogOpen(false);
        }}
      />
    </Card>
  );
}

export default PolicyInventoryCard;
